import { For, Index, Match, Show, Switch, type JSX } from "solid-js"
import { useLanguage } from "@/context/language"
import { Bar, Skeleton } from "./skeleton"
import { TablerIcon } from "./tabler-icons"
import "./tokens.css"
import "./skeleton.css"
import "./workflow-steps.css"
import "./editor-skeleton.css"

// The report editor while it loads (DESIGN-LOG §25, §64), with the shared bars of §63: the frame of the view the URL
// opens (Preview, Editor or Workflow), and the parts that load after it (the canvas, the Workflow steps and the step's
// card). Each part is drawn in the classes of what lands there, so nothing moves when it does. The sign-in outline and
// the shell draw the same frame (page-skeleton.tsx), so a fresh load reads as one skeleton from the first frame.

type T = (key: string) => string
const useT = (): T => {
  const language = useLanguage()
  return (key) => language.t(key as Parameters<typeof language.t>[0])
}

export type EditorView = "preview" | "editor" | "workflow"
// The editor's own rule: no view on arrival means Preview.
export const editorView = (view?: string | null): EditorView => (view === "editor" || view === "workflow" ? view : "preview")

const STEPS = ["files", "update", "review"] as const

// The Workflow steps until his panel's summary is in: the three steps as they are drawn (their names are fixed), a pale
// circle for each status and a pale line for each "why or when".
export function WorkflowStepsSkeleton(props: { label?: string }) {
  const t = useT()
  return (
    <Skeleton class="dxe-wf-skel" label={props.label}>
      <ol aria-hidden="true">
        <Index each={[68, 76, 58]}>{(width, index) => (
          <li class="dxe-wf-step">
            <span class="dxe-wf-step-pick">
              <span class="dxe-wf-icon"><Bar round w={16} h={16} /></span>
              <span class="dxe-wf-step-text">
                <b>{t(`datox.v2.workflow.step.${STEPS[index]}`)}</b>
                <small><Bar w={`${width()}%`} /></small>
              </span>
            </span>
          </li>
        )}</Index>
      </ol>
    </Skeleton>
  )
}

// The step's side until then: the "Do this now" card (one sentence and its button), the step's title and why-line, and
// the body of Update the report (the step most reports open on): the last update, what it filled for this client, the
// pipeline line and the recent runs.
export function WorkflowDetailSkeleton(props: { label?: string }) {
  return (
    <Skeleton class="dxe-wf-skel dxe-wf-skel-detail" label={props.label}>
      <div class="dxe-wf-now">
        <span class="dxe-wf-icon"><Bar round w={16} h={16} /></span>
        <p><b><Bar w={320} /></b></p>
        <Bar class="dxe-wf-skel-btn" w={52} h={32} />
      </div>
      <div class="dxe-wf-detail-head">
        <div><h3><Bar w={150} /></h3><p><Bar w={300} /></p></div>
        <Bar class="dxe-wf-skel-btn" w={84} h={28} />
      </div>
      <div class="dxe-wf-detail-body">
        <p class="dxe-wf-lead"><Bar w="62%" /></p>
        <div class="dxe-wf-client">
          <Bar round w={16} h={16} />
          <span><Bar w={190} /></span>
          <Bar class="dxe-wf-skel-btn" w={98} h={28} />
        </div>
        <p class="dxe-wf-pipe"><Bar w={440} /></p>
        <p class="dxe-wf-skel-runs"><Bar w={110} /></p>
      </div>
    </Skeleton>
  )
}

// The Workflow view's frame: its head (the title is fixed, Run history waits), the steps and the step's side.
function WorkflowSkeleton() {
  const t = useT()
  return (
    <div class="dxe-wf">
      <header class="dxe-wf-head">
        <h2>{t("datox.v2.workflow.title")}</h2>
        <span class="dxe-wf-scope"><Bar w={210} /></span>
        <span class="dxe-wf-grow" />
        <span class="dxe-wf-ghost" data-skel><TablerIcon name="history" size={14} stroke-width="1.5" />{t("datox.v2.workflow.history")}</span>
      </header>
      <div class="dxe-wf-body">
        <div class="dxe-wf-steps"><WorkflowStepsSkeleton /></div>
        <div class="dxe-wf-detail"><WorkflowDetailSkeleton /></div>
      </div>
    </div>
  )
}

// The workspace card until his canvas is in: the page tabs on their band and the page. Preview shows it at 100% from
// the top, the Editor fitted in the middle (his fit). The page holds the factsheet's own boxes: the header band, four
// figures, and two columns of tables.
export function CanvasSkeleton(props: { view: "preview" | "editor"; label?: string }) {
  return (
    <Skeleton class="dxe-sk-card" data-view={props.view} label={props.label}>
      <div class="dxe-sk-tabs">
        <For each={[132, 104, 104, 92]}>{(width) => <span class="dxe-sk-tab"><Bar w={width} /></span>}</For>
        <span class="dxe-sk-grow" />
        <Bar w={44} />
      </div>
      <div class="dxe-sk-canvas">
        <div class="dxe-sk-sheet">
          <div class="dxe-sk-sheet-head"><span><Bar line={false} w="38%" /><Bar line={false} w="22%" /></span><i /></div>
          <div class="dxe-sk-sheet-row">
            <For each={[52, 64, 46, 58]}>{(width) => <div class="dxe-sk-box"><Bar line={false} w={`${width}%`} /><Bar line={false} w="70%" h={18} /></div>}</For>
          </div>
          <div class="dxe-sk-sheet-cols">
            <For each={[0, 1]}>{() => (
              <div class="dxe-sk-col">
                <div class="dxe-sk-box"><Bar line={false} w="44%" /><span class="dxe-sk-table" /></div>
                <div class="dxe-sk-box"><Bar line={false} w="52%" /><span class="dxe-sk-table" /></div>
              </div>
            )}</For>
          </div>
        </div>
      </div>
    </Skeleton>
  )
}

// A sheet's rows until they are read (the file preview, a table's Rows in Technical details): the canvas grid's header
// row and 28px rows, its row numbers and columns. `note`: real progress (downloading 3 of 29 MB, reading the sheet),
// shown over it as it is.
export function GridSkeleton(props: { label?: string; class?: string; note?: JSX.Element }) {
  return (
    <Skeleton class={props.class ? `dxe-sk-grid ${props.class}` : "dxe-sk-grid"} label={props.label}>
      {props.note}
      <div class="dxe-sk-grid-rows" aria-hidden="true">
        <For each={Array.from({ length: 24 })}>{(_, row) => (
          <div class="dxe-sk-grid-row" data-head={row() === 0 || undefined}>
            <span class="dxe-sk-grid-n"><Show when={row()}><Bar w={row() < 10 ? 7 : 13} /></Show></span>
            <For each={[34, 22, 18, 26, 16, 20]}>{(width, column) => <span><Bar w={`${row() === 0 ? width + 14 : width + ((row() * 7 + column() * 13) % 22)}%`} /></span>}</For>
          </div>
        )}</For>
      </div>
    </Skeleton>
  )
}

// A document's lines until it is read (a PDF, a picture or slides in the file preview).
export function LinesSkeleton(props: { label?: string; class?: string }) {
  return (
    <Skeleton class={props.class ? `dxe-sk-lines ${props.class}` : "dxe-sk-lines"} label={props.label}>
      <For each={[56, 88, 82, 90, 64, 0, 84, 78, 46]}>{(width) => width ? <Bar w={`${width}%`} /> : <span class="dxe-sk-gap" />}</For>
    </Skeleton>
  )
}

// Settings, History and Send while their content loads, in the dialog's own shapes: Send is the PDF option and the
// review heading over the reviewers; Settings and History are labelled fields and rows.
export function DialogSkeleton(props: { mode: string; label?: string }) {
  return (
    <Skeleton class="dxe-sk-dialog" label={props.label}>
      <Show when={props.mode === "share"} fallback={
        <For each={[{ label: 84, h: 32 }, { label: 112, h: 60 }, { label: 0, h: 84 }]}>{(row) => (
          <div class="dxe-sk-field">
            <Show when={row.label}><Bar w={row.label} /></Show>
            <span class="dxe-sk-input" style={{ height: `${row.h}px` }} />
          </div>
        )}</For>
      }>
        <div class="dxe-send-option">
          <Bar class="dxe-send-icon" w={36} h={36} />
          <div class="dxe-send-text"><strong><Bar w={150} /></strong><p><Bar w="74%" /></p></div>
          <Bar w={116} h={32} />
        </div>
        <h3 class="dxe-send-review"><Bar w={120} /></h3>
        <For each={[64, 48]}>{(width) => <div class="dxe-sk-field"><Bar w={`${width}%`} /><span class="dxe-sk-input" style={{ height: "32px" }} /></div>}</For>
      </Show>
    </Skeleton>
  )
}

// The whole editor until the report is in (§25): the tool rail (its tools, none in Workflow), the top bar, and the view
// the URL opens. The Editor has its panel (the assistant: its head, the empty chat, the composer) beside the card; the
// Technical details bar's row is there in the Editor and Workflow, not in Preview (§46).
export function EditorSkeleton(props: { view: EditorView; label?: string }) {
  const t = useT()
  return (
    <Skeleton class="dxe-skeleton" data-view={props.view} label={props.label ?? t("datox.v2.editor.loading")}>
      <div class="dxe-sk-rail" aria-hidden="true">
        <span class="dxe-sk-mark"><img src="/lesha-bank-mark.png" alt="" /></span>
        <Show when={props.view !== "workflow"}>
          <For each={[42, 50, 44, 26, 30]}>{(width) => <span class="dxe-sk-tool"><Bar w={20} h={20} /><Bar w={width} /></span>}</For>
        </Show>
        <span class="dxe-sk-tool dxe-sk-settings"><Bar w={20} h={20} /><Bar w={34} /><Bar w={40} /></span>
        <span class="dxe-sk-avatar"><Bar round w={28} h={28} /></span>
      </div>
      <div class="dxe-sk-main">
        <div class="dxe-sk-bar" aria-hidden="true">
          <Bar w={16} h={16} /><Bar w={112} /><Bar w={96} /><Bar w={44} /><Bar w={148} /><Bar w={112} />
          <span class="dxe-sk-grow" />
          <Bar w={86} /><Bar w={168} h={28} /><Bar w={68} h={30} />
        </div>
        <div class="dxe-sk-body">
          <Switch>
            <Match when={props.view === "workflow"}><div class="dxe-sk-wf"><WorkflowSkeleton /></div></Match>
            <Match when={props.view === "editor"}>
              <div class="dxe-sk-panel" aria-hidden="true">
                <div class="dxe-sk-panel-head"><Bar w={128} /><span class="dxe-sk-grow" /><Bar w={16} h={16} /><Bar w={16} h={16} /></div>
                <div class="dxe-sk-intro">
                  <Bar w={32} h={32} />
                  <Bar class="dxe-sk-intro-title" w={200} />
                  <Bar w={270} />
                  <ul><For each={[62, 54, 58, 68]}>{(width) => <li><Bar w={16} h={16} /><Bar w={`${width}%`} /></li>}</For></ul>
                </div>
                <div class="dxe-sk-composer" />
              </div>
              <CanvasSkeleton view="editor" />
            </Match>
            <Match when><CanvasSkeleton view="preview" /></Match>
          </Switch>
        </div>
      </div>
      <Show when={props.view !== "preview"}>
        <div class="dxe-sk-foot" aria-hidden="true"><TablerIcon name="terminal-2" size={16} stroke-width="1.5" /><span>{t("datox.v2.workflow.technical")}</span></div>
      </Show>
    </Skeleton>
  )
}
